<template>
  <!-- Authenticated: logout, profile/switch, wallet -->
  <div
    v-if="authStore.isAuthenticated"
    class="flex justify-start gap-8 px-4"
    data-testid="quickmenu-authenticated"
  >
    <div class="text-center">
      <button
        class="mx-auto block size-10 rounded-full bg-transparent p-2 transition-colors hover:bg-black/10"
        :aria-label="t('general/non_special_text/logout')"
        type="button"
        @click="logout()"
      >
        <fa-icon class="size-6 text-rhino" :icon="faSignOutAlt" />
      </button>
      <p class="mb-0 capitalize">
        {{ t('general/non_special_text/logout') }}
      </p>
    </div>

    <div class="group text-center">
      <a
        data-testid="sidebar-region-switcher"
        class="relative mx-auto my-1 block size-8 cursor-pointer rounded-full border border-transparent p-0 transition-colors group-hover:border-brand"
        :aria-label="t('profile/modal/switch_accounts')"
        @click="openUserSwitchModal"
      >
        <common-avatar class="size-7.5" />
        <span
          class="absolute -right-2 -bottom-1 whitespace-nowrap rounded-sm bg-depth-0 px-1 py-0.5 text-xs font-bold leading-none transition-colors group-hover:bg-brand"
        >
          <span
            data-testid="sidebar-region-current"
            class="group-hover:hidden text-mouse font-bold"
          >
            {{ getRegionLabel(activeCustomerRegion) }}
          </span>
          <fa-icon
            class="hidden size-2.5 text-onyx group-hover:inline"
            :icon="faArrowsRepeat"
          />
        </span>
      </a>
      <p v-if="activeCustomer != null" class="mb-0 truncate max-w-20">
        {{
          t('general/non_special_texts/account_id', {
            id: activeCustomer.id,
          })
        }}
      </p>
    </div>

    <div class="text-center">
      <nuxt-link
        :to="rechargeLink"
        class="mx-auto block size-10 rounded-full p-2 transition-colors hover:bg-black/10"
        :aria-label="t('general/menu/item/recharge_credit')"
        @click="onRechargeLinkClick"
      >
        <fa-icon class="size-6 text-rhino" :icon="faCreditCard" />
      </nuxt-link>
      <p class="mb-0 capitalize">
        {{ walletBalance }}
      </p>
    </div>
  </div>

  <!-- Anonymous: login + register -->
  <div v-else class="flex justify-start gap-8 px-4" data-testid="quickmenu">
    <div class="text-center">
      <button
        class="mx-auto block size-10 rounded-full bg-transparent p-1 transition-colors hover:bg-black/10"
        :aria-label="t('general/non_special_text/login')"
        type="button"
        @click="login()"
      >
        <fa-icon class="size-6 text-rhino" :icon="faUser" />
      </button>
      <p class="mb-0 capitalize">
        {{ t('general/non_special_text/login') }}
      </p>
    </div>

    <div class="text-center">
      <nuxt-link
        :to="`/${locale}/register`"
        class="mx-auto block size-10 rounded-full p-1 transition-colors hover:bg-black/10"
        :aria-label="t('frontend/register')"
        :prefetch="false"
      >
        <fa-icon class="size-6 text-rhino" :icon="faPenToSquare" />
      </nuxt-link>
      <p class="mb-0 capitalize">
        {{ t('frontend/register') }}
      </p>
    </div>
  </div>
</template>

<script setup lang="ts">
import { OPEN_MODAL_SYMBOL } from '@/components/navigation/symbols';
import {
  faArrowsRepeat,
  faCreditCard,
  faUser,
} from '@fortawesome/pro-regular-svg-icons';
import { faPenToSquare, faSignOutAlt } from '@fortawesome/pro-solid-svg-icons';
import useAuth from '~/composables/useAuth';
import type { GpRegion } from '~/plugins/grpc/universal/types';

const { t, locale } = useI18n();
const authStore = useAuthStore();
const currencyStore = useCurrencyStore();
const { login, logout } = useAuth();

// Authenticated-only state (safe to compute — values are empty/default when anonymous)
const activeCustomer = computed(() => authStore.activeCustomer);
const activeCustomerRegion = computed<GpRegion>(() => authStore.activeRegion);

const { format } = useNumberMoneyFormatting();
const walletBalance = computed(() => {
  if (activeCustomer.value?.wallet == null)
    return `${currencyStore.currency} 0`;
  return format(activeCustomer.value.wallet);
});

const isModalOpen = inject(OPEN_MODAL_SYMBOL, ref(false));
const openUserSwitchModal = () => {
  isModalOpen.value = true;
};

const { rechargeLink, onRechargeLinkClick } = useBalanceFocus();
</script>
